import { useEffect, useMemo, useState } from 'react'
import { useAuth, useClerk } from '@clerk/clerk-react'
import { Navigate, createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useHttpClientSetup } from '@/hooks/use-http-client-setup'
import { useAuthSync } from '@/hooks/use-auth-sync'
import { postToOgsmSsoBridge, resolveOgsmNext } from '@/lib/ogsm-sync-url'
import {
  selectIsProfileReady,
  selectRole,
  useUserStore,
} from '@/stores/user.store'
import { getRedirectTarget } from '@/services/auth.service'
import type { RedirectTarget } from '@/types/api'
import logoCompassImg from '@/assets/images/greybull-logo-full.svg'

type AuthRedirectSearch = {
  next?: string
}

export const Route = createFileRoute('/auth/redirect')({
  validateSearch: (search: Record<string, unknown>): AuthRedirectSearch => {
    const next = search.next
    return typeof next === 'string' && next.length > 0 ? { next } : {}
  },
  component: AuthRedirectPage,
})

function AuthRedirectPage() {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth()
  const clerk = useClerk()
  const isHttpReady = useHttpClientSetup()
  const { isLoading: isAuthSyncLoading, error: authSyncError } = useAuthSync()
  const isProfileReady = useUserStore(selectIsProfileReady)
  const role = useUserStore(selectRole)
  const { next: rawNext } = Route.useSearch()
  const [target, setTarget] = useState<RedirectTarget | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [requestKey, setRequestKey] = useState(0)

  const ogsmBaseUrl = import.meta.env.VITE_OGSM_URL?.trim() || ''

  // A valid OGSM `next` overrides the default redirect target so that direct
  // OGSM deep links round-trip through Compass without losing their
  // destination. Rejected/missing values fall through to /auth/redirect-target.
  const forcedOgsmNext = useMemo(
    () => resolveOgsmNext(rawNext, ogsmBaseUrl),
    [rawNext, ogsmBaseUrl]
  )

  // When a validated OGSM `next` is present we force the bridge target,
  // bypassing the /auth/redirect-target lookup so direct OGSM deep links work
  // even when the user's default target is Compass.
  const effectiveTarget: RedirectTarget | null =
    forcedOgsmNext !== null ? 'ogsm' : target

  useEffect(() => {
    let cancelled = false

    if (!isLoaded || !isSignedIn || !isHttpReady || !isProfileReady) {
      return
    }

    if (forcedOgsmNext !== null) {
      // Forced OGSM next overrides the default target — no backend call needed.
      return
    }

    void (async () => {
      try {
        const response = await getRedirectTarget()
        if (!cancelled) {
          setTarget(response.target)
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(
            fetchError instanceof Error
              ? fetchError.message
              : 'Failed to resolve redirect target'
          )
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    isLoaded,
    isSignedIn,
    isHttpReady,
    isProfileReady,
    requestKey,
    userId,
    forcedOgsmNext,
  ])

  useEffect(() => {
    if (effectiveTarget !== 'ogsm' || !ogsmBaseUrl) {
      return
    }

    let cancelled = false
    void (async () => {
      try {
        await postToOgsmSsoBridge({
          getToken,
          ogsmBaseUrl,
          next: forcedOgsmNext ?? undefined,
          openInNewTab: false,
        })
      } catch (bridgeError) {
        if (cancelled) return
        setError(
          bridgeError instanceof Error
            ? bridgeError.message
            : 'OGSM SSO bridge failed'
        )
      }
    })()

    return () => {
      cancelled = true
    }
  }, [effectiveTarget, ogsmBaseUrl, getToken, userId, forcedOgsmNext])

  if (!isLoaded) {
    return <RedirectLoading />
  }

  if (!isSignedIn) {
    // Preserve `next` so the sign-in page can re-attach it to Clerk's
    // post-sign-in redirect; otherwise the original OGSM deep link is lost.
    return <Navigate to="/" search={rawNext ? { next: rawNext } : undefined} />
  }

  // Error must take precedence over both loading and `target === 'ogsm'` —
  // otherwise a failed profile sync or bridge POST can leave us stuck on the
  // loader.
  const redirectError = error ?? authSyncError

  if (redirectError) {
    return (
      <RedirectError
        message={redirectError}
        onRetry={() => {
          setTarget(null)
          setError(null)
          setRequestKey((value) => value + 1)
        }}
        onSignOut={() => clerk.signOut({ redirectUrl: '/' })}
      />
    )
  }

  if (!isHttpReady || isAuthSyncLoading || !isProfileReady) {
    return <RedirectLoading />
  }

  if (effectiveTarget === 'compass') {
    return role?.startsWith('PORTCO_') ? (
      <Navigate to="/portal" />
    ) : (
      <Navigate to="/companies" />
    )
  }

  if (effectiveTarget === 'ogsm') {
    if (!ogsmBaseUrl) {
      return (
        <RedirectError
          message="OGSM redirect URL is not configured."
          onRetry={() => {
            setTarget(null)
            setError(null)
            setRequestKey((value) => value + 1)
          }}
          onSignOut={() => clerk.signOut({ redirectUrl: '/' })}
        />
      )
    }

    return <RedirectLoading />
  }

  if (effectiveTarget === 'none') {
    return (
      <RedirectError
        message="Your account does not have access to Compass or OGSM."
        onRetry={() => {
          setTarget(null)
          setError(null)
          setRequestKey((value) => value + 1)
        }}
        onSignOut={() => clerk.signOut({ redirectUrl: '/' })}
      />
    )
  }

  return <RedirectLoading />
}

function RedirectLoading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <img
        src={logoCompassImg}
        alt="Compass"
        className="h-25 w-auto animate-pulse"
      />
    </div>
  )
}

function RedirectError({
  message,
  onRetry,
  onSignOut,
}: {
  message: string
  onRetry: () => void
  onSignOut: () => Promise<unknown>
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <img src={logoCompassImg} alt="Compass" className="h-25 w-auto" />
      <Typography as="p" className="text-destructive">
        {message}
      </Typography>
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={onRetry}>
          Retry
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void onSignOut()
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  )
}
